import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, message, Space, Typography, Card, Tag, Spin, Empty, Modal, Upload, Alert } from "antd";
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CommentOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useAuth from "../hooks/useAuth";
import instance from "../api/cmsapi";
import QuillHtmlDisplay from "./QuillHtmlDisplay";
import PetitionDocumentPanel from "./PetitionDocumentPanel";
import DpatCircleProgressLoader from "./DpatCircleProgressLoader";
import { quillFormats, quillModules } from "../utils/quillEditorConfig";
import { syncPetitionToDocumentTracker } from "../utils/petitionTrackerApi";
import {
  canViewerSeePetitionContent,
  derivePetitionRowStatus,
  getPetitionStatusMeta,
} from "../utils/petitionHelpers";

const DPAT_PRIMARY = "#16325a";
const TYPE = "DPAT";

// Petition submission deadline: September 23rd, 2026, 11:59 PM
const PETITION_DEADLINE = new Date("2026-09-23T23:59:59");

function isPetitionDeadlinePassed() {
  return new Date() > PETITION_DEADLINE;
}

function getPetitionDeadlineWarning() {
  const now = new Date();
  const daysUntilDeadline = Math.ceil((PETITION_DEADLINE - now) / (1000 * 60 * 60 * 24));
  
  if (daysUntilDeadline <= 0) {
    return null; // Deadline passed
  }
  
  if (daysUntilDeadline <= 3) {
    return {
      type: "error",
      message: `Urgent: Petition submission deadline is ${daysUntilDeadline} day${daysUntilDeadline === 1 ? '' : 's'} away!`,
      description: "The deadline for submitting petitions for 2025 Assessment is September 23rd, 2026, 11:59 PM. Please submit your petition as soon as possible.",
    };
  }
  
  if (daysUntilDeadline <= 7) {
    return {
      type: "warning",
      message: `Reminder: Petition submission deadline is ${daysUntilDeadline} days away`,
      description: "The deadline for submitting petitions for 2025 Assessment is September 23rd, 2026, 11:59 PM.",
    };
  }
  
  return null; // No warning needed
}

function normalizeUserRole(role) {
  return String(role || "").replace(/\s+/g, "_").toUpperCase();
}

function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

function formatCommentDate(date) {
  if (!date) return "—";
  if (Array.isArray(date)) return date.filter((x) => x != null).join("/");
  return String(date);
}

function Petition({ year, districtId, districtName = "", assessmentStatus }) {
  const { user } = useAuth();
  const [editorContent, setEditorContent] = useState("");
  const [fetchLoading, setFetchLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [existingCommentId, setExistingCommentId] = useState(null);
  const [comments, setComments] = useState([]);
  const [committeeResponse, setCommitteeResponse] = useState(null);
  /** null = not answered; "yes" | "no" after district user chooses */
  const [petitionChoice, setPetitionChoice] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [trackerStatusText, setTrackerStatusText] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);

  const currentUsername = user?.user?.username || "";
  const currentFullName = user?.user?.fullName || "";
  const isDistrictUser = Boolean(
    user?.user?.userRoles?.some((role) =>
      ["Superuser", "DPAT DISTRICT USERS"].includes(role.name)
    )
  );

  const tableCommentedId = useMemo(
    () => (year != null && districtId != null ? `${year}petition${districtId}` : null),
    [year, districtId]
  );

  const committeeTableId = useMemo(
    () => (year != null && districtId != null ? `${year}petitioncommittee${districtId}` : null),
    [year, districtId]
  );

  const districtPetition = useMemo(
    () =>
      comments.find(
        (comment) =>
          comment.tableCommented === tableCommentedId &&
          String(comment.districtId) === String(districtId) &&
          comment.userRole === "DDUSER"
      ) ?? null,
    [comments, tableCommentedId, districtId]
  );

  const canSubmitNewPetition = useCallback(() => {
    if (!isDistrictUser) return false;
    if (!districtPetition) return true;
    return districtPetition.username === currentUsername;
  }, [isDistrictUser, districtPetition, currentUsername]);

  useEffect(() => {
    setPetitionChoice(null);
    setEditorContent("");
    setExistingCommentId(null);
    setComments([]);
    setCommitteeResponse(null);
    setPdfFile(null);
    setTrackerStatusText("");
    setFetchLoading(true);
  }, [districtId, year]);

  useEffect(() => {
    let cancelled = false;

    const fetchPetitionData = async () => {
      if (!districtId || year == null || !tableCommentedId || !committeeTableId) {
        if (!cancelled) setFetchLoading(false);
        return;
      }

      try {
        setFetchLoading(true);

        const [petitionResult, committeeResult] = await Promise.allSettled([
          instance.get(`comments/table/${districtId}/${year}/${TYPE}/${tableCommentedId}`),
          instance.get(`comments/table/${districtId}/${year}/${TYPE}/${committeeTableId}`),
        ]);

        if (cancelled) return;

        const petitionData =
          petitionResult.status === "fulfilled" ? petitionResult.value?.data : [];
        const petition =
          (Array.isArray(petitionData) ? petitionData : []).find(
            (row) => normalizeUserRole(row.userRole) === "DDUSER"
          ) ?? (Array.isArray(petitionData) && petitionData.length > 0 ? petitionData[0] : null);

        const committeeData =
          committeeResult.status === "fulfilled" ? committeeResult.value?.data : [];
        const committee =
          (Array.isArray(committeeData) ? committeeData : []).find(
            (row) =>
              normalizeUserRole(row.userRole) === "DPC" &&
              String(row.districtId) === String(districtId) &&
              row.tableCommented === committeeTableId
          ) ?? null;

        if (petition && normalizeUserRole(petition.userRole) === "DDUSER") {
          setComments([petition]);
          setEditorContent(petition.comments || "");
          setExistingCommentId(petition.id);
          setPetitionChoice("yes");
        } else {
          setComments([]);
        }

        setCommitteeResponse(committee);
      } catch (error) {
        console.error("Failed to fetch petition data:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });
        if (!cancelled) message.error("Failed to load petition");
      } finally {
        if (!cancelled) setFetchLoading(false);
      }
    };

    fetchPetitionData();
    return () => {
      cancelled = true;
    };
  }, [districtId, year, tableCommentedId, committeeTableId]);

  const handlePdfBeforeUpload = (file) => {
    const isPdf =
      file.type === "application/pdf" || String(file.name || "").toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      message.error("Only PDF files can be uploaded for the petition document.");
      return Upload.LIST_IGNORE;
    }

    setPdfFile(file);
    return false;
  };

  const handleSave = async () => {
    if (stripHtml(editorContent) === "") {
      message.error("Petition text cannot be empty");
      return;
    }
    if (!pdfFile) {
      message.error("Please upload a PDF petition document before submitting.");
      return;
    }
    if (!currentUsername || !districtId || year == null) {
      message.error("User or district information is missing");
      return;
    }
    if (!isDistrictUser) {
      message.error("Only DPAT DISTRICT USERS can submit a petition here");
      return;
    }
    if (!canSubmitNewPetition()) {
      message.error("Only one DPAT DISTRICT user can submit a petition for this district.");
      return;
    }
    if (isPetitionDeadlinePassed()) {
      message.error("The petition submission deadline has passed.");
      return;
    }

    const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
    const payload = {
      id: existingCommentId || 0,
      username: currentUsername,
      fullName: currentFullName,
      userRole: "DDUSER",
      type: "DPAT",
      districtId,
      year,
      tableCommented: tableCommentedId,
      comments: editorContent,
      commentDate,
      updateDate: commentDate,
    };

    try {
      setSaveLoading(true);
      setTrackerStatusText("Submitting petition…");

      // Save petition comment first
      const response = await instance.post("comments", payload);
      const savedComment = response.data;

      // Sync to document tracker
      setTrackerStatusText("Uploading to Assessment Document Tracker…");
      
      const trackerResult = await syncPetitionToDocumentTracker({
        orgUnitId: districtId,
        districtName,
        year,
        petitionPlainText: stripHtml(editorContent),
        pdfFile,
        onStatus: setTrackerStatusText,
      }).catch((trackerError) => {
        console.error("Failed to sync petition document tracker:", trackerError);
        const trackerMessage =
          trackerError?.response?.data?.message ||
          trackerError?.message ||
          "The document could not be registered in the Assessment Document Tracker.";
        message.warning(`Petition saved, but tracker sync failed: ${trackerMessage}`);
        return null;
      });

      // Update comment with tracker metadata if sync succeeded
      if (trackerResult) {
        setTrackerStatusText("Completing submission…");
        await instance.post("comments", {
          ...payload,
          id: savedComment.id,
          dddpDataDate: commentDate,
          dddpData: {
            ...(savedComment?.dddpData || {}),
            petitionTracker: trackerResult,
          },
        });
      }

      setComments([savedComment]);
      setExistingCommentId(savedComment.id);
      setPetitionChoice("yes");
      setPdfFile(null);
      setProgressModalOpen(false);
      message.success("Petition submitted successfully");
    } catch (error) {
      console.error("Failed to save petition:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      message.error(`Failed to save petition: ${error.response?.data?.message || error.message}`);
      setProgressModalOpen(false);
    } finally {
      setSaveLoading(false);
      setTrackerStatusText("");
    }
  };

  const handleConfirmSubmit = () => {
    setConfirmModalOpen(false);
    setProgressModalOpen(true);
    // Let the caution modal close before starting the progress popup
    window.setTimeout(() => {
      handleSave();
    }, 150);
  };

  const handleSubmitClick = () => {
    if (stripHtml(editorContent) === "") {
      message.error("Petition text cannot be empty");
      return;
    }
    if (!pdfFile) {
      message.error("Please upload a PDF petition document before submitting.");
      return;
    }
    if (!currentUsername || !districtId || year == null) {
      message.error("User or district information is missing");
      return;
    }
    if (!isDistrictUser) {
      message.error("Only DPAT DISTRICT USERS can submit a petition here");
      return;
    }
    if (!canSubmitNewPetition()) {
      message.error("Only one DPAT DISTRICT user can submit a petition for this district.");
      return;
    }
    if (isPetitionDeadlinePassed()) {
      message.error("The petition submission deadline has passed.");
      return;
    }

    setConfirmModalOpen(true);
  };

  const showIntentPrompt =
    isDistrictUser &&
    !fetchLoading &&
    !existingCommentId &&
    petitionChoice === null &&
    canSubmitNewPetition() &&
    !isPetitionDeadlinePassed();

  const showEditor =
    isDistrictUser &&
    canSubmitNewPetition() &&
    assessmentStatus &&
    petitionChoice === "yes" &&
    !existingCommentId &&
    !isPetitionDeadlinePassed();

  const showDeadlinePassed =
    isDistrictUser &&
    !fetchLoading &&
    !existingCommentId &&
    isPetitionDeadlinePassed();

  const showSubmittedCard =
    Boolean(districtPetition) &&
    (isDistrictUser || canViewerSeePetitionContent({ isDistrictUser, committeeResponse }));

  const petitionStatusMeta = useMemo(() => {
    if (!districtPetition || !isDistrictUser) return null;
    const statusKey = derivePetitionRowStatus({
      assessmentStatus: "Completed",
      districtPetition,
      committeeResponse,
    });
    return getPetitionStatusMeta(statusKey);
  }, [districtPetition, committeeResponse, isDistrictUser]);

  const showDeclinedState =
    isDistrictUser && petitionChoice === "no" && !existingCommentId && !fetchLoading;

  if (!fetchLoading && !isDistrictUser && !districtPetition && !assessmentStatus) {
    return null;
  }

  if (showDeclinedState) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8">
        <Card className="text-center shadow-sm" bordered>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Typography.Text type="secondary">
                You chose not to submit a petition for this assessment.
              </Typography.Text>
            }
          />
        </Card>
      </div>
    );
  }

  if (showDeadlinePassed && !districtPetition) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8">
        <Card className="text-center shadow-sm" bordered>
          <div
            className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}
          >
            <CloseCircleOutlined style={{ fontSize: 22, color: "#ef4444" }} />
          </div>
          <Typography.Title level={4} style={{ margin: "0 0 12px", color: "#ef4444" }}>
            Petition Submission Closed
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: 0, maxWidth: 480, marginInline: "auto" }}>
            The deadline for submitting petitions for 2025 Assessment was September 23rd, 2026, 11:59 PM.
            <br />
            Petition submissions are no longer accepted.
          </Typography.Paragraph>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-8">
      <Card
        className="shadow-sm"
        bordered
        styles={{ body: { padding: "28px 24px 32px" } }}
      >
        <div className="mb-2 text-center">
          <div
            className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(22, 50, 90, 0.08)" }}
          >
            <FileTextOutlined style={{ fontSize: 22, color: DPAT_PRIMARY }} />
          </div>
          <Typography.Title level={3} style={{ margin: 0, color: DPAT_PRIMARY, letterSpacing: "0.04em" }}>
            PETITION
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: "8px 0 0", maxWidth: 480, marginInline: "auto" }}>
            District users may submit a formal petition after the assessment is completed.
          </Typography.Paragraph>
        </div>

        {fetchLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Spin size="large" tip="Loading petition…" />
          </div>
        ) : (
          <div className="mx-auto flex w-full flex-col gap-8">
            <section className="flex w-full flex-col items-center">
            {showIntentPrompt && (
              <div
                className="mb-8 w-full rounded-xl border border-dashed px-6 py-8 text-center"
                style={{ borderColor: "#d4dce6", background: "#f8fafc" }}
              >
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                  Submit a petition?
                </Typography.Title>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
                  Do you want to send a petition for this district assessment?
                </Typography.Paragraph>
                <Space wrap size="middle" className="justify-center">
                  <Button
                    type="primary"
                    size="large"
                    icon={<CheckCircleOutlined />}
                    onClick={() => setPetitionChoice("yes")}
                    style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
                  >
                    Yes, send a petition
                  </Button>
                  <Button size="large" icon={<CloseCircleOutlined />} onClick={() => setPetitionChoice("no")}>
                    No
                  </Button>
                </Space>
              </div>
            )}

            {showEditor && (
              <div className="mb-6 w-full max-w-none">
                {getPetitionDeadlineWarning() && (
                  <Alert
                    type={getPetitionDeadlineWarning().type}
                    message={getPetitionDeadlineWarning().message}
                    description={getPetitionDeadlineWarning().description}
                    showIcon
                    closable
                    style={{ marginBottom: 16 }}
                  />
                )}
                <div className="mb-3 text-center">
                  <Tag color="processing">Draft petition</Tag>
                </div>
                <div className="w-full overflow-hidden rounded-lg border border-[#e6ebf1] bg-white">
                  <ReactQuill
                    value={editorContent}
                    onChange={setEditorContent}
                    modules={quillModules}
                    formats={quillFormats}
                    className="petition-quill-editor"
                    style={{ height: "480px", marginBottom: "50px", width: "100%" }}
                    readOnly={saveLoading}
                  />
                </div>
                <div className="mb-6 w-full rounded-lg border border-[#e6ebf1] bg-[#fafbfc] px-5 py-4">
                  <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
                    Attach petition document (PDF)
                  </Typography.Text>
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
                    Upload the signed petition PDF. It will be registered in the Assessment Document Tracker after your petition text is saved.
                  </Typography.Paragraph>
                  <Upload
                    accept=".pdf,application/pdf"
                    maxCount={1}
                    beforeUpload={handlePdfBeforeUpload}
                    onRemove={() => setPdfFile(null)}
                    fileList={
                      pdfFile
                        ? [
                            {
                              uid: pdfFile.uid || pdfFile.name,
                              name: pdfFile.name,
                              status: "done",
                            },
                          ]
                        : []
                    }
                  >
                    <Button icon={<UploadOutlined />} disabled={saveLoading}>
                      Select PDF file
                    </Button>
                  </Upload>
                </div>
                <div className="mt-4 flex justify-center">
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleSubmitClick}
                    disabled={saveLoading}
                    style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY, minWidth: 200 }}
                  >
                    Send petition
                  </Button>
                </div>
              </div>
            )}

            {showSubmittedCard && (
              <div
                className="w-full rounded-xl border px-5 py-5"
                style={{ borderColor: "#e6ebf1", background: "#fafbfc" }}
              >
                <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    Petition submitted
                  </Tag>
                  {isDistrictUser && petitionStatusMeta && petitionStatusMeta.key !== "none" && (
                    <Tag className={petitionStatusMeta.badgeClass}>{petitionStatusMeta.label}</Tag>
                  )}
                </div>
                <QuillHtmlDisplay html={districtPetition.comments} />
                <PetitionDocumentPanel
                  districtId={districtId}
                  year={year}
                  districtName={districtName}
                  petitionComment={districtPetition}
                />
                <Typography.Text
                  type="secondary"
                  style={{ display: "block", marginTop: 12, textAlign: "right", fontSize: 12 }}
                >
                  {formatCommentDate(districtPetition.commentDate)}
                </Typography.Text>
              </div>
            )}

            {!isDistrictUser && !districtPetition && assessmentStatus && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No petition has been submitted for this district yet."
              />
            )}
            </section>

          </div>
        )}
      </Card>

      {!fetchLoading && committeeResponse && (isDistrictUser || canViewerSeePetitionContent({ isDistrictUser, committeeResponse })) && (
        <section className="w-full">
          <div className="mb-3 flex items-center justify-center gap-2">
            <CommentOutlined style={{ color: DPAT_PRIMARY }} />
            <Typography.Title level={5} style={{ margin: 0 }}>
              Complaints Resolution Committee response
            </Typography.Title>
          </div>
          <Card
            className="shadow-sm"
            bordered
            styles={{ body: { padding: "20px 24px", background: "#f6ffed" } }}
          >
            <div className="mb-4 flex justify-center">
              <Tag color="success" icon={<CheckCircleOutlined />}>
                Complaints Resolution Committee — Response submitted
              </Tag>
            </div>
            <QuillHtmlDisplay html={committeeResponse.comments} />
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginTop: 12, textAlign: "right", fontSize: 12 }}
            >
              {formatCommentDate(committeeResponse.updateDate || committeeResponse.commentDate)}
            </Typography.Text>
          </Card>
        </section>
      )}

      <Modal
        open={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleConfirmSubmit}
            style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
          >
            Yes, submit
          </Button>,
        ]}
        destroyOnClose
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleOutlined style={{ color: "#faad14" }} />
            Submit petition?
          </span>
        }
      >
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          Once submitted, this petition cannot be edited. Please review your petition text and
          attached PDF carefully. Are you sure you want to continue?
        </Typography.Paragraph>
      </Modal>

      <Modal
        open={progressModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        destroyOnClose
        title="Submitting petition"
      >
        <DpatCircleProgressLoader
          done={!saveLoading}
          label={trackerStatusText || "Submitting petition…"}
        />
      </Modal>
    </div>
  );
}

export default Petition;
